import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { useNotificationTemplates } from '@/queries/useNotificationTemplates';
import { useAuthStore } from '@/stores/useAuthStore';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/dateFormatter';

function renderTextWithChips(text: string) {
  const parts = text.split(/(\{\{\s*\w+\s*\}\})/g);
  return parts.map((part, i) => {
    const match = part.match(/\{\{\s*(\w+)\s*\}\}/);
    if (match) {
      return (
        <Chip
          key={i}
          label={match[1]}
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ mx: 0.5, verticalAlign: 'middle' }}
        />
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export function NotificationTemplatesPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Admin']);
  const { data: templates, isLoading, isError } = useNotificationTemplates();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplateId, setMenuTemplateId] = useState<number | null>(null);

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, templateId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuTemplateId(templateId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuTemplateId(null);
  };

  const handleEdit = () => {
    if (menuTemplateId) {
      navigate(paths.admin.notificationTemplateDetail.replace(':id', String(menuTemplateId)));
    }
    handleCloseMenu();
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Notification Templates
      </Typography>

      <Section>
        <SectionHeader>NOTIFICATIONS LIST</SectionHeader>
        <SectionCard>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load notification templates.
            </Alert>
          ) : !templates || templates.length === 0 ? (
            <EmptyStateMessage message="No notification templates found." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell>Modified By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id} hover>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>{renderTextWithChips(template.subject)}</TableCell>
                      <TableCell>{template.trigger}</TableCell>
                      <TableCell>{formatDate(template.updatedAt)}</TableCell>
                      <TableCell>{template.updatedByName || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, template.id)}
                            aria-label={`Actions for ${template.subject}`}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Section>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
      </Menu>
    </Box>
  );
}

export default NotificationTemplatesPage;
